/**
 * Core data types for the Cartographer skeleton model.
 *
 * Ported from cw-cartographer/cartographer/core.py. Uses three.js Matrix4 /
 * Vector3 for transforms to keep viewer integration trivial; recognition
 * helpers can read elements directly from Matrix4.elements.
 */

import { Matrix4, Vector3 } from 'three';
import {
  buildBoneRefData,
  type BoneRefBuildResult,
} from './boneRefs';

// ─── Euler order ────────────────────────────────────────────────────────

export enum EulerOrder {
  XYZ = 'xyz',
  XZY = 'xzy',
  YXZ = 'yxz',
  YZX = 'yzx',
  ZXY = 'zxy',
  ZYX = 'zyx',
}

export type EulerOrderSource = 'transformData' | 'objectRotation' | 'default';

// ─── Bone properties ────────────────────────────────────────────────────

export type AxisLimits = [number | null, number | null, number | null];
export type AxisLocked = [boolean, boolean, boolean];

const AXIS_KEYS = ['x', 'y', 'z'] as const;

export class RotationLimits {
  min: AxisLimits;
  max: AxisLimits;
  locked: AxisLocked;

  constructor(
    min: AxisLimits = [null, null, null],
    max: AxisLimits = [null, null, null],
    locked: AxisLocked = [false, false, false],
  ) {
    this.min = min;
    this.max = max;
    this.locked = locked;
  }

  get hasAny(): boolean {
    return (
      this.min.some((v) => v !== null) ||
      this.max.some((v) => v !== null) ||
      this.locked.some(Boolean)
    );
  }

  toDict(): Record<string, unknown> {
    const d: Record<string, unknown> = {};
    for (let i = 0; i < 3; i++) {
      const key = AXIS_KEYS[i];
      if (this.locked[i]) {
        d[key] = 'locked';
      } else if (this.min[i] !== null || this.max[i] !== null) {
        d[key] = [this.min[i], this.max[i]];
      }
    }
    return d;
  }

  static fromDict(data: Record<string, unknown>): RotationLimits {
    if ('min' in data || 'max' in data) {
      return RotationLimits._fromLegacyDict(data);
    }
    const mins: AxisLimits = [null, null, null];
    const maxs: AxisLimits = [null, null, null];
    const locks: AxisLocked = [false, false, false];
    for (let i = 0; i < 3; i++) {
      const val = data[AXIS_KEYS[i]];
      if (val === 'locked') {
        locks[i] = true;
      } else if (Array.isArray(val) && val.length === 2) {
        mins[i] = val[0] as number | null;
        maxs[i] = val[1] as number | null;
      }
    }
    return new RotationLimits(mins, maxs, locks);
  }

  private static _fromLegacyDict(
    data: Record<string, unknown>,
  ): RotationLimits {
    const rawMin = (data.min as (number | null)[] | undefined) ?? [
      null,
      null,
      null,
    ];
    const rawMax = (data.max as (number | null)[] | undefined) ?? [
      null,
      null,
      null,
    ];
    return new RotationLimits(
      [rawMin[0] ?? null, rawMin[1] ?? null, rawMin[2] ?? null],
      [rawMax[0] ?? null, rawMax[1] ?? null, rawMax[2] ?? null],
    );
  }
}

export class PreferredAngles {
  x: number;
  y: number;
  z: number;

  constructor(x = 0, y = 0, z = 0) {
    this.x = x;
    this.y = y;
    this.z = z;
  }

  toDict(): Record<string, number> {
    const d: Record<string, number> = {};
    if (this.x !== 0) d.x = Math.round(this.x * 10000) / 10000;
    if (this.y !== 0) d.y = Math.round(this.y * 10000) / 10000;
    if (this.z !== 0) d.z = Math.round(this.z * 10000) / 10000;
    return d;
  }

  static fromDict(data: Record<string, unknown>): PreferredAngles {
    return new PreferredAngles(
      Number(data.x ?? 0),
      Number(data.y ?? 0),
      Number(data.z ?? 0),
    );
  }
}

export class BoneProperties {
  rotationLimits: RotationLimits | null;
  preferredAngles: PreferredAngles | null;

  constructor(
    rotationLimits: RotationLimits | null = null,
    preferredAngles: PreferredAngles | null = null,
  ) {
    this.rotationLimits = rotationLimits;
    this.preferredAngles = preferredAngles;
  }

  get hasAny(): boolean {
    return (
      (this.rotationLimits !== null && this.rotationLimits.hasAny) ||
      this.preferredAngles !== null
    );
  }

  toDict(): Record<string, unknown> {
    const d: Record<string, unknown> = {};
    if (this.rotationLimits?.hasAny) {
      d.rotation_limits = this.rotationLimits.toDict();
    }
    if (this.preferredAngles !== null) {
      d.preferred_angles = this.preferredAngles.toDict();
    }
    return d;
  }

  static fromDict(data: Record<string, unknown>): BoneProperties {
    const rot = data.rotation_limits as Record<string, unknown> | undefined;
    const pa = data.preferred_angles as Record<string, unknown> | undefined;
    return new BoneProperties(
      rot ? RotationLimits.fromDict(rot) : null,
      pa ? PreferredAngles.fromDict(pa) : null,
    );
  }
}

// ─── Bone ───────────────────────────────────────────────────────────────

export class Bone {
  index: number;
  name: string;
  /** As authored in the source file / for downstream systems; matches THREE bone.name when loaders keep no separate name. */
  sourceName: string;
  /** Canonical internal mapping ref (short ref when globally unique, else full path ref). */
  internalRef: string;
  /** Full path ref in internal name-space. */
  internalFullPathRef: string;
  /** Canonical source mapping ref (short ref when globally unique, else full path ref). */
  sourceRef: string;
  /** Full path ref in source name-space. */
  sourceFullPathRef: string;
  parent: number | null;
  children: number[];
  restLocal: Matrix4;
  restWorld: Matrix4;
  length: number;
  direction: Vector3;
  depth: number;
  subtreeSize: number;
  deforming: boolean;
  auxiliary: boolean;
  side: 'Left' | 'Right' | null;
  properties: BoneProperties | null;
  poseLocal: Matrix4;
  posedWorld: Matrix4;
  eulerOrder: EulerOrder;
  eulerOrderSource: EulerOrderSource;

  constructor(init: {
    index: number;
    name: string;
    sourceName?: string;
    parent: number | null;
    children?: number[];
    restLocal?: Matrix4;
    deforming?: boolean;
    auxiliary?: boolean;
    eulerOrder?: EulerOrder;
    eulerOrderSource?: EulerOrderSource;
  }) {
    this.index = init.index;
    this.name = init.name;
    this.sourceName = init.sourceName ?? init.name;
    this.internalRef = this.name;
    this.internalFullPathRef = this.name;
    this.sourceRef = this.sourceName;
    this.sourceFullPathRef = this.sourceName;
    this.parent = init.parent;
    this.children = init.children ?? [];
    this.restLocal = init.restLocal ?? new Matrix4();
    this.restWorld = new Matrix4();
    this.length = 0;
    this.direction = new Vector3();
    this.depth = 0;
    this.subtreeSize = 1;
    this.deforming = init.deforming ?? true;
    this.auxiliary = init.auxiliary ?? false;
    this.side = null;
    this.properties = null;
    this.poseLocal = new Matrix4();
    this.posedWorld = new Matrix4();
    this.eulerOrder = init.eulerOrder ?? EulerOrder.XYZ;
    this.eulerOrderSource = init.eulerOrderSource ?? 'default';
  }

  get hasSide(): boolean {
    return this.side !== null;
  }

  get isLeft(): boolean {
    return this.side === 'Left';
  }

  get isRight(): boolean {
    return this.side === 'Right';
  }
}

// ─── Skeleton ───────────────────────────────────────────────────────────

const _tmpVec = new Vector3();
const _tmpVec2 = new Vector3();

export class Skeleton {
  bones: Bone[];
  root: number;
  /** World transform of the root bone's non-bone parent (e.g. Armature). */
  rootParentWorld: Matrix4;
  private _internalRefs: BoneRefBuildResult;
  private _sourceRefs: BoneRefBuildResult;
  private _hasDuplicateNames = false;
  private _hasDuplicateSourceNames = false;
  private _posedWorldScratch = new Matrix4();

  constructor(bones: Bone[], root: number, rootParentWorld?: Matrix4) {
    this.bones = bones;
    this.root = root;
    this.rootParentWorld = rootParentWorld ?? new Matrix4();
    this._internalRefs = buildBoneRefData([]);
    this._sourceRefs = buildBoneRefData([]);
    this.rebuildBoneRefCaches();
  }

  bone(idx: number): Bone {
    return this.bones[idx];
  }

  private rebuildBoneRefCaches(): void {
    const internalItems = this.bones.map((bone) => ({
      index: bone.index,
      parentIndex: bone.parent,
      name: bone.name,
    }));
    const sourceItems = this.bones.map((bone) => ({
      index: bone.index,
      parentIndex: bone.parent,
      name: bone.sourceName,
    }));

    this._internalRefs = buildBoneRefData(internalItems);
    this._sourceRefs = buildBoneRefData(sourceItems);

    this._hasDuplicateNames = [...this._internalRefs.nameCounts.values()].some(
      (count) => count > 1,
    );
    this._hasDuplicateSourceNames = [...this._sourceRefs.nameCounts.values()].some(
      (count) => count > 1,
    );

    for (const bone of this.bones) {
      bone.internalRef =
        this._internalRefs.canonicalRefByIndex[bone.index] ?? bone.name;
      bone.internalFullPathRef =
        this._internalRefs.fullPathRefByIndex[bone.index] ?? bone.name;
      bone.sourceRef =
        this._sourceRefs.canonicalRefByIndex[bone.index] ?? bone.sourceName;
      bone.sourceFullPathRef =
        this._sourceRefs.fullPathRefByIndex[bone.index] ?? bone.sourceName;
    }
  }

  get hasDuplicateNames(): boolean {
    return this._hasDuplicateNames;
  }

  get hasDuplicateSourceNames(): boolean {
    return this._hasDuplicateSourceNames;
  }

  isAmbiguousName(name: string): boolean {
    return (this._internalRefs.nameCounts.get(name) ?? 0) > 1;
  }

  isAmbiguousSourceName(name: string): boolean {
    return (this._sourceRefs.nameCounts.get(name) ?? 0) > 1;
  }

  findByName(name: string): number | null {
    const count = this._internalRefs.nameCounts.get(name) ?? 0;
    if (count !== 1) return null;
    return this._internalRefs.uniqueNameToIndex.get(name) ?? null;
  }

  findBySourceName(name: string): number | null {
    const count = this._sourceRefs.nameCounts.get(name) ?? 0;
    if (count !== 1) return null;
    return this._sourceRefs.uniqueNameToIndex.get(name) ?? null;
  }

  resolveBoneRefWithStatus(
    ref: string,
    options?: { namespace?: 'internal' | 'source' | 'either' },
  ): { index: number | null; status: 'resolved' | 'ambiguous' | 'not_found' } {
    const namespace = options?.namespace ?? 'either';
    const allowInternal = namespace === 'internal' || namespace === 'either';
    const allowSource = namespace === 'source' || namespace === 'either';
    let ambiguous = false;

    if (allowInternal) {
      const mapped = this._internalRefs.refToIndex.get(ref);
      if (mapped != null) return { index: mapped, status: 'resolved' };

      const count = this._internalRefs.nameCounts.get(ref) ?? 0;
      if (count === 1) {
        const idx = this._internalRefs.uniqueNameToIndex.get(ref);
        if (idx != null) return { index: idx, status: 'resolved' };
      } else if (count > 1) {
        ambiguous = true;
      }
    }

    if (allowSource) {
      const mapped = this._sourceRefs.refToIndex.get(ref);
      if (mapped != null) return { index: mapped, status: 'resolved' };

      const count = this._sourceRefs.nameCounts.get(ref) ?? 0;
      if (count === 1) {
        const idx = this._sourceRefs.uniqueNameToIndex.get(ref);
        if (idx != null) return { index: idx, status: 'resolved' };
      } else if (count > 1) {
        ambiguous = true;
      }
    }

    return { index: null, status: ambiguous ? 'ambiguous' : 'not_found' };
  }

  resolveBoneRef(
    ref: string,
    options?: { namespace?: 'internal' | 'source' | 'either' },
  ): number | null {
    return this.resolveBoneRefWithStatus(ref, options).index;
  }

  findByRef(
    ref: string,
    options?: { namespace?: 'internal' | 'source' | 'either' },
  ): number | null {
    return this.resolveBoneRef(ref, options);
  }

  refForBoneIndex(
    idx: number,
    options?: { useSourceNames?: boolean; forceFullPath?: boolean },
  ): string {
    const bone = this.bones[idx];
    if (!bone) return '';
    const useSource = options?.useSourceNames ?? false;
    const fullPath = options?.forceFullPath ?? false;
    if (useSource) {
      return fullPath ? bone.sourceFullPathRef : bone.sourceRef;
    }
    return fullPath ? bone.internalFullPathRef : bone.internalRef;
  }

  exportRefForInternalRef(
    internalRef: string,
    options?: { useSourceNames?: boolean; forceFullPath?: boolean },
  ): string | null {
    const idx = this.resolveBoneRef(internalRef, { namespace: 'internal' });
    if (idx === null) return null;
    return this.refForBoneIndex(idx, options);
  }

  internalRefForImportedBone(importedRef: string): string | null {
    const idx = this.resolveBoneRef(importedRef, { namespace: 'either' });
    if (idx === null) return null;
    return this.refForBoneIndex(idx, {
      useSourceNames: false,
      forceFullPath: false,
    });
  }

  /** Mapping JSON / downstream: internal (THREE) bone name → file/source name. */
  exportNameForInternal(internalName: string): string {
    const idx = this.resolveBoneRef(internalName, { namespace: 'internal' });
    if (idx === null) return internalName;
    return this.bones[idx].sourceName;
  }

  /** Resolve a bone key from imported JSON to the skeleton's internal bone name. */
  internalNameForImportedBone(nameFromFile: string): string {
    const idx = this.resolveBoneRef(nameFromFile, { namespace: 'either' });
    if (idx !== null) return this.bones[idx].name;
    return nameFromFile;
  }

  /** Propagate rest_local down the tree to compute rest_world. */
  computeWorldTransforms(): void {
    const dfs = (idx: number, parentWorld: Matrix4): void => {
      const b = this.bones[idx];
      b.restWorld.multiplyMatrices(parentWorld, b.restLocal);
      for (const child of b.children) dfs(child, b.restWorld);
    };
    dfs(this.root, this.rootParentWorld);
  }

  /** posed_world = parent_posed_world * rest_local * pose_local */
  computePosedWorldTransforms(): void {
    const tmp = this._posedWorldScratch;
    const dfs = (idx: number, parentPosed: Matrix4): void => {
      const b = this.bones[idx];
      tmp.multiplyMatrices(b.restLocal, b.poseLocal);
      b.posedWorld.multiplyMatrices(parentPosed, tmp);
      for (const child of b.children) dfs(child, b.posedWorld);
    };
    dfs(this.root, this.rootParentWorld);
  }

  resetPoses(): void {
    for (const b of this.bones) b.poseLocal.identity();
    this.computePosedWorldTransforms();
  }

  computeDepthsAndSubtrees(): void {
    const dfs = (idx: number, depth: number): number => {
      const b = this.bones[idx];
      b.depth = depth;
      let size = 1;
      for (const child of b.children) size += dfs(child, depth + 1);
      b.subtreeSize = size;
      return size;
    };
    dfs(this.root, 0);
  }

  computeLengthsAndDirections(): void {
    const positions = this.bones.map((b) => {
      const pos = new Vector3();
      pos.setFromMatrixPosition(b.restWorld);
      return pos;
    });

    for (const b of this.bones) {
      if (b.children.length > 0) {
        _tmpVec.subVectors(positions[b.children[0]], positions[b.index]);
        const len = _tmpVec.length();
        b.length = len;
        b.direction = len > 1e-8 ? _tmpVec.clone().divideScalar(len) : new Vector3();
      } else if (b.parent !== null) {
        _tmpVec.subVectors(positions[b.index], positions[b.parent]);
        const len = _tmpVec.length();
        b.length = len;
        b.direction = len > 1e-8 ? _tmpVec.clone().divideScalar(len) : new Vector3();
      } else {
        b.length = 0;
        b.direction = new Vector3();
      }
    }
  }

  ancestors(idx: number): number[] {
    const result: number[] = [];
    let current = this.bones[idx].parent;
    while (current !== null) {
      result.push(current);
      current = this.bones[current].parent;
    }
    return result;
  }

  descendants(idx: number): number[] {
    const result: number[] = [];
    const dfs = (node: number): void => {
      for (const child of this.bones[node].children) {
        result.push(child);
        dfs(child);
      }
    };
    dfs(idx);
    return result;
  }

  /** Get world-space position of a bone in the rest pose. */
  getBoneRestWorldPosition(idx: number): Vector3 {
    return _tmpVec2.setFromMatrixPosition(this.bones[idx].restWorld).clone();
  }

  /** Get world-space position of a bone in the current posed state. */
  getBonePosedWorldPosition(idx: number): Vector3 {
    return _tmpVec2.setFromMatrixPosition(this.bones[idx].posedWorld).clone();
  }

  /** Backward-compatible alias for rest-pose world position. */
  getBoneWorldPosition(idx: number): Vector3 {
    return this.getBoneRestWorldPosition(idx);
  }
}
